import { useEffect, useRef } from 'react';
import { Icon } from '../Icon';
import { Button } from '../Button/Button';
import './styles.css';

interface ConfirmationModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmationModal({
  isOpen,
  title,
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  variant = 'danger',
  onConfirm,
  onCancel,
}: ConfirmationModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  // Cerrar con Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onCancel();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onCancel]);

  // Cerrar al hacer clic fuera
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  if (!isOpen) return null;

  const iconName = variant === 'danger' ? 'alert-triangle' : variant === 'warning' ? 'alertCircle' : 'info';

  return (
    <div className="confirmation-modal__backdrop" onClick={handleBackdropClick} onKeyDown={(e) => { if (e.key === 'Escape') handleBackdropClick(e as unknown as React.MouseEvent<HTMLDivElement>); }}>
      <div className="confirmation-modal" ref={modalRef}>
        <div className={`confirmation-modal__icon confirmation-modal__icon--${variant}`}>
          <Icon name={iconName} size={24} />
        </div>
        <div className="confirmation-modal__content">
          <h3 className="confirmation-modal__title">{title}</h3>
          <p className="confirmation-modal__message">{message}</p>
        </div>
        <div className="confirmation-modal__actions">
          <Button variant="secondary" onClick={onCancel}>
            {cancelText}
          </Button>
          <Button 
            variant={variant === 'danger' ? 'danger' : 'primary'} 
            onClick={onConfirm}
          >
            {confirmText}
          </Button>
        </div>
      </div>
    </div>
  );
}
